import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import { ROW_HEIGHT_PX, formatRange } from "~/components/projects/ProjectsTimelineGrid";
import { cn } from "~/lib/utils";
import { TaskAssigneeIdentity } from "./atoms/TaskAssigneeIdentity";
import { TaskPriorityToken } from "./atoms/TaskPriorityToken";
import { deriveTaskPresentation } from "./taskPresentation";
import type { ProjectTaskViewProps } from "./taskViewTypes";

export interface TaskTimelineRow {
  readonly task: CmmdBoardTask;
  readonly dueDate: Date | null;
}

export function ProjectTaskTimelineWorkColumn(props: {
  readonly datedRows: ReadonlyArray<TaskTimelineRow>;
  readonly undatedRows: ReadonlyArray<TaskTimelineRow>;
  readonly threadIndex: ProjectTaskViewProps["threadIndex"];
  readonly pendingTaskIds: ReadonlySet<number>;
  readonly width: number;
  readonly onWidthChange: (px: number) => void;
  readonly onResetWidth: () => void;
  readonly onOpenTask: (taskId: number) => void;
}) {
  return (
    <div
      className="sticky left-0 z-30 shrink-0 border-r border-border bg-background"
      style={{ width: props.width }}
    >
      <div className="sticky top-0 z-20 flex h-11 items-center border-b border-border bg-background/95 px-4 backdrop-blur">
        <span className="text-xs font-semibold text-foreground">Tasks</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {props.datedRows.length + props.undatedRows.length} tasks
        </span>
      </div>
      <div>
        {props.datedRows.map((row) => (
          <TaskTimelineWorkRow
            key={row.task.id}
            row={row}
            threadIndex={props.threadIndex}
            pending={props.pendingTaskIds.has(row.task.id)}
            onOpenTask={props.onOpenTask}
          />
        ))}
        {props.undatedRows.length > 0 ? (
          <div
            className="flex items-center border-b border-border bg-muted px-4 text-[11px] font-medium uppercase text-muted-foreground"
            style={{ height: ROW_HEIGHT_PX }}
          >
            No due date
          </div>
        ) : null}
        {props.undatedRows.map((row) => (
          <TaskTimelineWorkRow
            key={row.task.id}
            row={row}
            threadIndex={props.threadIndex}
            pending={props.pendingTaskIds.has(row.task.id)}
            onOpenTask={props.onOpenTask}
          />
        ))}
      </div>
      <TaskTimelineResizeHandle
        width={props.width}
        onWidthChange={props.onWidthChange}
        onReset={props.onResetWidth}
      />
    </div>
  );
}

function TaskTimelineWorkRow(props: {
  readonly row: TaskTimelineRow;
  readonly threadIndex: ProjectTaskViewProps["threadIndex"];
  readonly pending: boolean;
  readonly onOpenTask: (taskId: number) => void;
}) {
  const presentation = deriveTaskPresentation(props.row.task, props.threadIndex);
  return (
    <button
      type="button"
      onClick={() => props.onOpenTask(props.row.task.id)}
      className={cn(
        "group flex w-full items-center gap-3 border-b border-border px-4 text-left transition-colors hover:bg-muted",
        props.pending ? "opacity-60" : null,
      )}
      style={{ height: ROW_HEIGHT_PX }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-foreground">{props.row.task.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{formatRange(null, props.row.dueDate)}</span>
          {presentation.resolvedThread ? (
            <span>{presentation.resolvedThread.agentStatus}</span>
          ) : null}
        </div>
      </div>
      <TaskPriorityToken priority={props.row.task.priority} />
      <TaskAssigneeIdentity
        name={presentation.assigneeName}
        avatarUrl={presentation.assigneeAvatar}
        kind={presentation.assigneeKind}
        agentId={presentation.assigneeAgentId}
        className="size-5"
      />
    </button>
  );
}

function TaskTimelineResizeHandle(props: {
  readonly width: number;
  readonly onWidthChange: (px: number) => void;
  readonly onReset: () => void;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: props.width };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    props.onWidthChange(dragRef.current.startWidth + event.clientX - dragRef.current.startX);
  };
  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize task column"
      tabIndex={0}
      className="group absolute inset-y-0 right-0 z-40 w-2 translate-x-1/2 cursor-col-resize touch-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={props.onReset}
    >
      <div className="absolute inset-y-0 left-1/2 w-px bg-transparent transition-colors group-hover:bg-primary/50" />
    </div>
  );
}
